import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';

import { map } from "rxjs/operators";
import { ClaimPointsInterface } from '../interfaces/claim-points.interface';
import { PageInterface } from '../interfaces/page.interface';
import { TokenResponseInterface } from '../interfaces/token-response.interface';
import { TransferPointsInterface } from '../interfaces/transfer-points.interface';
import { AccountNoteInterface } from '../interfaces/vrc/account-note.interface';
import { AccountInterface } from '../interfaces/vrc/account.interface';
import { FamilyStatusInterface } from '../interfaces/vrc/account/family-status.interface';
import { InterestInterface } from '../interfaces/vrc/account/interest.interface';
import { LoyalityDetailsInterface } from '../interfaces/vrc/account/loyality-details.interface';
import { CommunicationAllInterface } from '../interfaces/vrc/communication-all.interface';
import { CommunicationCallDetailsInterface } from '../interfaces/vrc/communication-call-details.interface';
import { CommunicationChatDetailsInterface } from '../interfaces/vrc/communication-chat-details.interface';
import { CommunicationCloseWorkItemRequestInterface } from '../interfaces/vrc/communication-close-work-item-request.interface';
import { CommunicationDfoDetailsInterface } from '../interfaces/vrc/communication-dfo-details.interface';
import { CommunicationEmailAttachmentRequestInterface } from '../interfaces/vrc/communication-email-attachment-request.interface';
import { CommunicationEmailAttachmentResponseInterface } from '../interfaces/vrc/communication-email-attachment-response.interface';
import { CommunicationEmailDetailsInterface } from '../interfaces/vrc/communication-email-details.interface';
import { CommunicationUpdateAccountRequestInterface } from '../interfaces/vrc/communication-update-account-request.interface';
import { CommunicationVoiceRecordingsResponseInterface } from '../interfaces/vrc/communication-voice-recordings-response.interface';
import { CommunicationInterface } from '../interfaces/vrc/communication.interface';
import { CreateLoyaltyInterface } from '../interfaces/vrc/create-loyalty.interface';
import { EmailValidationInterface } from '../interfaces/vrc/email-validation.interface';
import { OfferDetailsInterface } from '../interfaces/vrc/offer-details.interface';
import { OfferEmailInterface } from '../interfaces/vrc/offer-email.interface';
import { OfferValidationResponseInterface } from '../interfaces/vrc/offer-validation-response.interface';
import { PhoneValidationInterface } from '../interfaces/vrc/phone-validation.interface';
import { ReservationDetailsInterface } from '../interfaces/vrc/reservation-details.interface';
import { SendOfferInterface } from '../interfaces/vrc/send-offer.interface';
import { ValidOfferInterface } from '../interfaces/vrc/valid-offer.interface';
import { VoucherInterface } from '../interfaces/vrc/voucher.interface';
import { WebOfferPreviewInterface } from '../interfaces/vrc/web-offer-preview.interface';
import { ThreadInterface, ThreadsPaginatorInterface, ThreadsResponseInterface } from "../interfaces/vrc/threads.interface";
import { VrcConfigService } from './vrc-config.service';
import { ValTableState } from '@valamar/val-table';
import { WorkItemInterface } from '../interfaces/vrc/work-item.interface';
import { WorkItemStandardResponseInterface } from '../interfaces/vrc/work-item-standard-response.interface';

@Injectable()
export class VrcDataService {
    private http = inject(HttpClient);
    private config = inject(VrcConfigService);


    public getToken = (data: { sessionId: string, agentId: number, contactId: number }): Observable<TokenResponseInterface> => {
        return this.http.post<TokenResponseInterface>(this.config.getTokenUrl, data);
    }
    public getUserToken = (data: { userName: string, password: string }): Observable<TokenResponseInterface> => {
        return this.http.post<TokenResponseInterface>(this.config.getUserTokenUrl, data);
    }
    public refreshToken = (): Observable<TokenResponseInterface> => {
        return this.http.get<TokenResponseInterface>(this.config.refreshTokenUrl);
    }
    public getSubscription = (): Observable<string> => {
        return this.http.get<string>(this.config.subscriptionUrl, { responseType: "text" as "json" });
    }
    public getAccountById = (accountId: string, takeComm = 5, takeOffers = 5, takeReservations = 5): Observable<AccountInterface | null> => {
        if (!accountId) { return of(null) }
        return this.http.get<AccountInterface>(this.config.accountByIdUrl + "?Id=" + accountId + "&TakeComm=" + takeComm + "&TakeOffers=" + takeOffers + "&TakeReservations=" + takeReservations);
    }
    public getAccountByIds = (contactId: string, accountIds: string[]): Observable<AccountInterface[] | null> => {
        if (!accountIds) { return of(null) }
        return this.http.get<AccountInterface[]>(this.config.accountByIdsUrl + "?contactId=" + contactId + accountIds.map(x => "&accountIds=" + x).reduce((a, b) => a += b, ''));
    }
    public getAccountByEmail = (email: string, takeComm = 5, takeOffers = 5, takeReservations = 5): Observable<AccountInterface[]> => {
        return this.http.get<AccountInterface[]>(this.config.accountByEmailUrl + "?Email=" + encodeURIComponent(email) + "&TakeComm=" + takeComm + "&TakeOffers=" + takeOffers + "&TakeReservations=" + takeReservations);
    }
    public getAccountByEmailOrPhoneOrContactId = (email: string, phone: string, contactId: number, take: number, firstName: string, lastName: string): Observable<AccountInterface[]> => {
        return this.http.get<AccountInterface[]>(this.config.accountByEmailOrPhoneOrContactIdUrl + "?email=" + email + "&phone=" + phone + "&contactId=" + contactId + "&take=" + take + "&firstid=" + firstName + "&lastid=" + lastName);
    }
    public getAccountByVoucherCode = (email: string, takeComm = 5, takeOffers = 5, takeReservations = 5): Observable<AccountInterface[]> => {
        return this.http.get<AccountInterface[]>(this.config.accountByVoucherCodeUrl + "?VoucherCode=" + email + "&TakeComm=" + takeComm + "&TakeOffers=" + takeOffers + "&TakeReservations=" + takeReservations);
    }
    public getAccountByLoyaltiesEmail = (email: string, takeComm = 5, takeOffers = 5, takeReservations = 5): Observable<AccountInterface[]> => {
        return this.http.get<AccountInterface[]>(this.config.accountByLoyaltiesEmailUrl + "?Email=" + email + "&TakeComm=" + takeComm + "&TakeOffers=" + takeOffers + "&TakeReservations=" + takeReservations);
    }
    public postAccount = (account: AccountInterface): Observable<AccountInterface> => {
        return this.http.post<AccountInterface>(this.config.accountUrl, account);
    }
    public postAccountAndLoyalty = (account: AccountInterface): Observable<AccountInterface> => {
        return this.http.post<AccountInterface>(this.config.accountAndLoyaltyUrl, account);
    }
    public getAccountSearch = (params: ValTableState): Observable<PageInterface<any>> => {
        return this.http.get<PageInterface<any>>(this.config.accountUrl + '/search?data=' + encodeURIComponent(JSON.stringify(params)));
        // return this.http.get<PageModel>(this.config.accountUrl + '/search?data=' + JSON.stringify(params));
    }
    public getAccountByEmailSimple = (params: ValTableState): Observable<PageInterface<any>> => {
        return this.http.get<PageInterface<any>>(this.config.accountUrl + '/search?data=' + encodeURIComponent(JSON.stringify(params)));
        // return this.http.get<PageModel>(this.config.accountUrl + '/search?data=' + JSON.stringify(params));
    }
    public getOfferPage = (params: ValTableState | undefined): Observable<PageInterface<OfferDetailsInterface>> => {
        return this.http.get<PageInterface<OfferDetailsInterface>>(this.config.offerUrl + '/page?data=' + encodeURIComponent(JSON.stringify(params)));
    }
    public getOfferPageAll = (params: ValTableState | undefined): Observable<PageInterface<OfferDetailsInterface>> => {
        return this.http.get<PageInterface<OfferDetailsInterface>>(this.config.offerUrl + '/pageAll?data=' + encodeURIComponent(JSON.stringify(params)));
    }
    public getOfferSearch = (params: string): Observable<PageInterface<OfferDetailsInterface>> => {
        return this.http.get<PageInterface<OfferDetailsInterface>>(this.config.offerUrl + '/search?search=' + encodeURIComponent(params));
    }
    public getReservationPage = (params: ValTableState | undefined): Observable<PageInterface<ReservationDetailsInterface>> => {
        return this.http.get<PageInterface<ReservationDetailsInterface>>(this.config.reservationUrl + '/page?data=' + encodeURIComponent(JSON.stringify(params)));
    }
    public getReservationPageAll = (params: ValTableState | undefined): Observable<PageInterface<ReservationDetailsInterface>> => {
        return this.http.get<PageInterface<ReservationDetailsInterface>>(this.config.reservationUrl + '/pageAll?data=' + encodeURIComponent(JSON.stringify(params)));
    }
    public getReservationSearch = (params: string): Observable<PageInterface<ReservationDetailsInterface>> => {
        return this.http.get<PageInterface<ReservationDetailsInterface>>(this.config.reservationUrl + '/search?search=' + encodeURIComponent(params));
    }
    public getVoucherPage = (params: ValTableState): Observable<PageInterface<VoucherInterface>> => {
        return this.http.get<PageInterface<VoucherInterface>>(this.config.voucherUrl + '/page?data=' + encodeURIComponent(JSON.stringify(params)));
    }
    public getVoucherAllPage = (params: ValTableState): Observable<PageInterface<VoucherInterface>> => {
        return this.http.get<PageInterface<VoucherInterface>>(this.config.voucherUrl + '/pageAll?data=' + encodeURIComponent(JSON.stringify(params)));
    }
    public getVoucherSearch = (search: string): Observable<PageInterface<VoucherInterface>> => {
        return this.http.get<PageInterface<VoucherInterface>>(this.config.voucherUrl + '/search?search=' + encodeURIComponent(search));
    }
    public getVoucherById = (id: string): Observable<VoucherInterface> => {
        return this.http.get<VoucherInterface>(this.config.voucherUrl + '/byId?id=' + id);
    }
    public getLoyalty = (accountId: string): Observable<LoyalityDetailsInterface[]> => {
        return this.http.get<LoyalityDetailsInterface[]>(this.config.loyaltyUrl + '/getByAccountId/' + accountId);
    }
    public postLoyalty = (loyalty: CreateLoyaltyInterface): Observable<AccountInterface> => {
        return this.http.post<AccountInterface>(this.config.createLoyaltyUrl, loyalty);
    }
    public getInterests = (): Observable<InterestInterface[]> => {
        return this.http.get<InterestInterface[]>(this.config.interestUrl);
    }
    public getAdvertisingPermissions = (): Observable<{ key: string, label: string }[]> => {
        return this.http.get<{ key: string, label: string }[]>(this.config.advertisingPermissionsUrl);
    }
    public getProducts = (): Observable<{ key: string, label: string }[]> => {
        return this.http.get<{ key: string, label: string }[]>(this.config.productsUrl);
    }
    public getFamilyStatuses = (): Observable<FamilyStatusInterface[]> => {
        return this.http.get<FamilyStatusInterface[]>(this.config.familyStatusUrl);
    }
    public getOffer = (offerId: string): Observable<SendOfferInterface> => {
        return this.http.get<SendOfferInterface>(this.config.getOfferUrl + '/' + offerId);
    }
    public cancelOffer = (offerId: string): Observable<SendOfferInterface> => {
        return this.http.get<SendOfferInterface>(this.config.cancelOfferUrl + '/' + offerId);
    }
    public confirmOffer = (offerId: string): Observable<SendOfferInterface> => {
        return this.http.get<SendOfferInterface>(this.config.confirmOfferUrl + '/' + offerId);
    }
    public sendOffer = (offerEmail: OfferEmailInterface): Observable<boolean> => {
        return this.http.post<boolean>(this.config.offerEmailUrl, offerEmail);
    }
    public getWebOfferLink = (offerId: string): Observable<WebOfferPreviewInterface> => {
        return this.http.get<WebOfferPreviewInterface>(this.config.getWebOfferLinkUrl + '/' + offerId);
    }
    public transferPoints = (transferPointsPayload: TransferPointsInterface): Observable<void> => {
        return this.http.post<void>(this.config.transferPointsUrl, transferPointsPayload);
    }
    public claimPoints = (claimPointsPayload: ClaimPointsInterface): Observable<any> => {
        return this.http.post<any>(this.config.claimPointsUrl, claimPointsPayload);
    }
    public resendLoyaltyConfirmation = (loyaltyProfileId: string, email: string): Observable<void> => {
        return this.http.post<void>(this.config.resendLoyaltyConfirmationUrl, { loyaltyProfileId: loyaltyProfileId, email: email });
    }
    public loyaltyForgottenPassword = (loyaltyProfileId: string, email: string): Observable<void> => {
        return this.http.post<void>(this.config.loyaltyForgottenPasswordUrl, { loyaltyProfileId: loyaltyProfileId, email: email });
    }
    public loyaltyNewEmail = (loyaltyCardNumber: string, currentEmail: string, newEmail: string): Observable<void> => {
        return this.http.post<void>(this.config.loyaltyNewEmailUrl, { loyaltyCardNumber: loyaltyCardNumber, currentEmail: currentEmail, newEmail: newEmail });
    }
    public getCommunicationPage = (params: ValTableState): Observable<PageInterface<CommunicationInterface>> => {
        return this.http.get<PageInterface<CommunicationInterface>>(this.config.communicationUrl + '/page?data=' + encodeURIComponent(JSON.stringify(params)));
    }
    public getCommunicationAllPage = (params: ValTableState | undefined): Observable<PageInterface<CommunicationAllInterface>> => {
        return this.http.get<PageInterface<CommunicationAllInterface>>(this.config.communicationUrl + '/pageAll?data=' + encodeURIComponent(JSON.stringify(params)));
    }
    public getCommunicationSearch = (search: string): Observable<PageInterface<CommunicationAllInterface>> => {
        return this.http.get<PageInterface<CommunicationAllInterface>>(this.config.communicationUrl + '/search?search=' + encodeURIComponent(search));
    }
    public getCommunicationThreads = (params: ValTableState | undefined): Observable<ThreadInterface[]> => {
        return this.http.get<ThreadInterface[]>(this.config.communicationUrl + '/threads?data=' + encodeURIComponent(JSON.stringify(params)));
    }

    public getCommunicationThreadsAggregation = (params: ValTableState | undefined): Observable<ThreadInterface[]> => {
        return this.http.get<ThreadInterface[]>(this.config.communicationUrl + '/threads/aggregation?data=' + JSON.stringify(params));
    }

    public getCommunicationThreadsPaginator = (params: ValTableState | undefined): Observable<ThreadsPaginatorInterface> => {
        return this.http.get<ThreadsResponseInterface>(this.config.communicationUrl + '/threads/paging?data=' + JSON.stringify(params))
            .pipe(
                map((response) => ({
                    items: response.items,
                    page: params?.first,
                    hasMorePages: response.skip + response.limit < response.total
                } as ThreadsPaginatorInterface))

            );
    }

    public getCommunicationDetails = (contactId: number, childGuid?: string): Observable<CommunicationChatDetailsInterface | CommunicationEmailDetailsInterface | CommunicationCallDetailsInterface | CommunicationDfoDetailsInterface> => {
        return this.http.get<CommunicationChatDetailsInterface | CommunicationEmailDetailsInterface | CommunicationCallDetailsInterface | CommunicationDfoDetailsInterface>(this.config.communicationUrl + '/details?contactId=' + contactId + (childGuid ? '&childGuid=' + childGuid : ''));
    }
    public getEmailDetails = (contactId: number): Observable<CommunicationEmailDetailsInterface> => {
        return this.http.get<CommunicationEmailDetailsInterface>(this.config.communicationUrl + '/emailDetails?contactId=' + contactId);
    }
    public getChatDetails = (contactId: number): Observable<CommunicationChatDetailsInterface> => {
        return this.http.get<CommunicationChatDetailsInterface>(this.config.communicationUrl + '/chatDetails?contactId=' + contactId);
    }
    public getCallDetails = (contactId: number): Observable<CommunicationCallDetailsInterface> => {
        return this.http.get<CommunicationCallDetailsInterface>(this.config.communicationUrl + '/callDetails?contactId=' + contactId);
    }
    public closeCommunication = (params: CommunicationCloseWorkItemRequestInterface): Observable<void> => {
        return this.http.post<void>(this.config.communicationUrl + '/endContact', params);
    }
    public getEmailAttachment = (params: CommunicationEmailAttachmentRequestInterface): Observable<CommunicationEmailAttachmentResponseInterface> => {
        return this.http.get<CommunicationEmailAttachmentResponseInterface>(this.config.communicationUrl + '/getEmailAttachment?data=' + encodeURIComponent(JSON.stringify(params)));
    }
    public getEmailAttachmentForPaymentEvents = (link: string): Observable<CommunicationEmailAttachmentResponseInterface> => {
        return this.http.get<CommunicationEmailAttachmentResponseInterface>(this.config.paymentUrl + '/getEmailAttachmentForPaymentEvents?link=' + encodeURIComponent(link));
    }
    public getRecordingFiles = (recordings: Array<{ fileName: string, fullFileName: string }>, contactId?: number): Observable<CommunicationVoiceRecordingsResponseInterface> => {
        return this.http.get<CommunicationVoiceRecordingsResponseInterface>(
            this.config.communicationUrl + '/getRecordings?recordings=' + encodeURIComponent(JSON.stringify(recordings)) +
            "&contactId=" + contactId);
    }
    public updateCommunicationAccount = (params: CommunicationUpdateAccountRequestInterface): Observable<boolean> => {
        return this.http.post<boolean>(this.config.communicationUrl + '/updateAccount', params);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public retrieveHierarchyForEmployee = (valamarArtistID: string): Observable<any> => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.http.get<any>(this.config.retrieveHierarchyForEmployeeUrl + 'retrieveHierarchyForEmployee?ValamarArtistID=' + valamarArtistID);
    }
    public getAccountByLoyalityparant = (loyalityParantId: string): Observable<AccountInterface> => {
        return this.http.get<AccountInterface>(this.config.loyaltyUrl + '/getAccount/' + loyalityParantId);
    }
    public getLoyalityByLoyalityId = (loyalityId: string): Observable<LoyalityDetailsInterface> => {
        return this.http.get<LoyalityDetailsInterface>(this.config.loyaltyUrl + '/getById/' + loyalityId);
    }
    public validOffer = (validOffer: ValidOfferInterface): Observable<OfferValidationResponseInterface> => {
        return this.http.post<OfferValidationResponseInterface>(this.config.validateOfferUrl, validOffer);
    }
    public validateEmail = (email: string): Observable<EmailValidationInterface> => {
        return this.http.get<EmailValidationInterface>(this.config.validateEmailUrl + '/' + email);
    }
    public validatePhone = (phone: string): Observable<PhoneValidationInterface> => {
        return this.http.get<PhoneValidationInterface>(this.config.validatePhoneUrl + '/' + phone);
    }
    public getAccountNotes = (accountId: string): Observable<AccountNoteInterface[]> => {
        return this.http.get<AccountNoteInterface[]>(this.config.accountNoteUrl + '/' + accountId);
    }
    public GetNoteCountSinceDate = (accountId: string, date: string): Observable<number> => {
        return this.http.get<number>(this.config.accountNoteUrl + '/' + accountId + '/' + date);
    }
    public addAccountNote = (accountNote: AccountNoteInterface): Observable<AccountNoteInterface> => {
        return this.http.post<AccountNoteInterface>(this.config.accountNoteUrl, accountNote);
    }
    public updateAccountNote = (accountNote: AccountNoteInterface): Observable<AccountNoteInterface> => {
        return this.http.put<AccountNoteInterface>(this.config.accountNoteUrl, accountNote);
    }
    public deleteAccountNote = (accountNoteId: string): Observable<AccountNoteInterface> => {
        return this.http.delete<AccountNoteInterface>(this.config.accountNoteUrl + '/' + accountNoteId);
    }

    public getWorkItem = (workItemId: string): Observable<WorkItemInterface> => {
        return this.http.get<WorkItemInterface>(this.config.workItemUrl + '?workItemId=' + workItemId);
    }
    public patchWorkItem = (workItemId: string, result: string): Observable<WorkItemStandardResponseInterface> => {
        return this.http.patch<WorkItemStandardResponseInterface>(this.config.workItemUrl + '/confirm?workItemId=' + workItemId + '&result=' + result, null);
    }
    public makeCallWorkItem = (workItemId: string,phoneNumber:string): Observable<WorkItemStandardResponseInterface> => {
        return this.http.get<WorkItemStandardResponseInterface>(this.config.workItemUrl + '/makecall?phoneNumber=' + encodeURIComponent(phoneNumber) + '&workItemId=' + workItemId);
    }
}
